import React from 'react';

import {MetricEntry} from '@types';
import {
    Chart,
    LineElement,
    LineController,
    LinearScale,
    CategoryScale,
    Tooltip,
    registerables,
} from 'chart.js';

import {Line} from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import {
    calculateEloLevelName,
    convertEloToChartLevel,
    getShortLevelName,
} from '@utils/elo';
import {useTheme} from '@components/theme/useTheme';
import {useTranslation} from 'react-i18next';
Chart.register(
    LineController,
    LineElement,
    LinearScale,
    CategoryScale,
    Tooltip,
    ...registerables
);

export const RatingDynamicChart = ({
    ratingData,
    preview,
    fitContainer = false,
    compact = false,
}: {
    ratingData: MetricEntry[];
    preview?: boolean;
    fitContainer?: boolean;
    compact?: boolean;
}) => {
    const {t} = useTranslation();
    const isDark = useTheme() === 'dark';
    const tickColor = isDark ? '#a3a3a3' : '#737373';
    const gridColor = isDark
        ? 'rgba(255, 255, 255, 0.10)'
        : 'rgba(0, 0, 0, 0.10)';
    const data = {
        datasets: [
            {
                label: t('common.rating'),
                tension: 0.35,
                cubicInterpolationMode: 'monotone' as const,
                borderWidth: compact ? 2 : 3,
                pointRadius: 0,
                pointHoverRadius: 4,
                pointHitRadius: 10,
                data: ratingData.map(row => ({
                    x: new Date(`${row.label}T00:00:00`).getTime(),
                    y: convertEloToChartLevel(row.amount),
                })),
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: fitContainer ? false : preview,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    title: (items: {dataIndex: number}[]) => {
                        const date = ratingData[items[0]?.dataIndex]?.label;
                        return date
                            ? new Date(`${date}T00:00:00`).toLocaleDateString(
                                  undefined,
                                  {year: 'numeric', month: 'short', day: 'numeric'}
                              )
                            : '';
                    },
                    label: (context: {dataIndex: number}) =>
                        `${t('common.rating')}: ${calculateEloLevelName(ratingData[context.dataIndex].amount, true)}`,
                },
            },
        },
        scales: {
            x: {
                type: 'time' as const,
                display: !preview,
                ticks: {
                    color: tickColor,
                    ...(compact ? {maxTicksLimit: 4, maxRotation: 0} : {}),
                },
                grid: {color: gridColor},
                border: {color: gridColor},
            },
            y: {
                grid: {color: gridColor},
                border: {color: gridColor},
                ticks: {
                    color: tickColor,
                    stepSize: 1,
                    callback: (value: string | number) => {
                        return getShortLevelName(value as number);
                    },
                },
            },
        },
    };

    return <Line id="ratingDynamic" data={data} options={options} />;
};
